import React from 'react'
import { DatePicker } from "antd";
import dayjs from 'dayjs';
import { useDispatch,useSelector } from "react-redux";
import { CURRENT_MONTH_ENABLE_DAY } from "../constants/common";
import { setSelectedMonthYear, setSelectedStartDateEndDate } from "../slices/commonReducer";
import {
    Row,
    Col,
    Breadcrumb,
} from "react-bootstrap";
import { NavLink, useParams } from "react-router-dom";

function DatePickerComponent({ hideDatepicker, title,submitFunction }) {
    const dispatch = useDispatch();
    const dayjsPackage = dayjs();
    const { client_id } = useParams();
    const { selectedStartDate, selectedEndDate } = useSelector((state) => state.common);

    // Helper function to parse Redux date format to { month, year }
    const parseReduxDate = (dateStr) => {
        if (!dateStr) return { month: null, year: null };
        
        // If it's a string in "MM-YYYY" format
        if (typeof dateStr === 'string' && dateStr.includes('-')) {
            const [month, year] = dateStr.split('-');
            return { month: parseInt(month), year: parseInt(year) };
        }
        
        // If it's already an object with month/year properties
        if (typeof dateStr === 'object' && dateStr.month && dateStr.year) {
            return { month: dateStr.month, year: dateStr.year };
        }
        
        return { month: null, year: null };
    };

    // Get current periods from Redux
    const fromPeriod = parseReduxDate(selectedStartDate);
    const toPeriod = parseReduxDate(selectedEndDate);

    // handle change in DatePicker - updates Redux immediately
    const handlePeriodChange = (type, date) => {
        if (!date) return;
        const year = dayjs(date).year();
        const month = dayjs(date).month() + 1; // dayjs is 0-based
        
        // Format as "MM-YYYY" string to match API payload format
        const formattedMonth = month.toString().padStart(2, '0');
        const newPeriodString = `${formattedMonth}-${year}`;
        
        if (type === "from") {
            dispatch(
                setSelectedStartDateEndDate({
                    start_date: newPeriodString,
                    end_date: selectedEndDate,
                })
            );
        } else {
            dispatch(
                setSelectedStartDateEndDate({
                    start_date: selectedStartDate,
                    end_date: newPeriodString,
                })
            );
        }
    };

    // disable future dates
    const disabledDate = (current) => {
        const currentMonth = dayjsPackage.month();
        const currentYear = dayjsPackage.year();
        if (
            current.year() > currentYear ||
            (current.year() === currentYear && current.month() > currentMonth) ||
            (current.year() === currentYear &&
                current.month() === currentMonth &&
                dayjsPackage.date() < CURRENT_MONTH_ENABLE_DAY)
        ) {
            return true;
        }
        return false;
    };

    // validation logic - simplified to only check From period <= To period
    function validatePeriod() {
        if (!selectedStartDate || !selectedEndDate) return false;

        // Parse the string format "MM-YYYY" directly
        const fromDate = dayjs(selectedStartDate, "MM-YYYY");
        const toDate = dayjs(selectedEndDate, "MM-YYYY");

        // Only validate that From period is not after To period
        return !toDate.isBefore(fromDate);
    }

    // submit handler - now just validates since Redux is already updated
    function submitDate() {
        if (!validatePeriod()) {
            alert("From period must be less than or equal to To period");
            return;
        }

        submitFunction()
    }

    return (
        <div>
            <div
                className={`flex gap-x-10 justify-between items-center bg-gray-100 p-3 rounded-lg ${hideDatepicker && "hidden"
                    }`}
            >
                <Row className="justify-content-center">
                    <Col xs="auto" className="text-center">
                        <Breadcrumb className="mt-3">
                            <Breadcrumb.Item linkAs={NavLink} linkProps={{ to: "/" }}>
                                Dashboard
                            </Breadcrumb.Item>
                            <Breadcrumb.Item linkAs={NavLink} linkProps={{ to: "/clients" }}>
                                Clients
                            </Breadcrumb.Item>
                            <Breadcrumb.Item
                                linkAs={NavLink}
                                linkProps={{ to: `/clients/tax-forms/${client_id}` }}
                            >
                                Tax-Forms
                            </Breadcrumb.Item>
                            <Breadcrumb.Item active>{title}</Breadcrumb.Item>
                        </Breadcrumb>
                    </Col>
                </Row>

                <div className="flex gap-x-10">
                    {/* From Period */}
                    <div className="flex flex-col">
                        <span className="text-sm font-medium mb-1">From Period</span>
                        <DatePicker
                            picker="month"
                            className="min-w-[10rem]"
                            format="MMMM YYYY"
                            onChange={(date) => handlePeriodChange("from", date)}
                            value={
                                fromPeriod.month && fromPeriod.year
                                    ? dayjs(`${fromPeriod.year}-${fromPeriod.month}-01`)
                                    : null
                            }
                            disabledDate={disabledDate}
                        />
                    </div>

                    {/* To Period */}
                    <div className="flex flex-col">
                        <span className="text-sm font-medium mb-1">To Period</span>
                        <DatePicker
                            picker="month"
                            className="min-w-[10rem]"
                            format="MMMM YYYY"
                            onChange={(date) => handlePeriodChange("to", date)}
                            value={
                                toPeriod.month && toPeriod.year
                                    ? dayjs(`${toPeriod.year}-${toPeriod.month}-01`)
                                    : null
                            }
                        />
                    </div>

                    <button
                        onClick={submitDate}
                        className="bg-[#F37920] px-4 py-2 rounded-sm text-white self-end fw-bold"
                    >
                        Submit
                    </button>
                </div>
            </div>
        </div>
    );
}

export default DatePickerComponent;